import { create } from 'zustand';
import { toast } from 'react-toastify';
import StudyOffersService, { BackendStudyOffer } from '@/services/offers/StudyOffersService';
import { dashboardService } from '@/services/dashboard/DashboardService';
import { subscribeOffers, subscribeOfferAcceptedDetailed, subscribeOfferCancelled, subscribeOfferExpired } from '@/services/offers/OffersRealtime';
import { useAuthStore } from '@/stores';

// Simple local cache for distances by contact_id
const DIST_CACHE_KEY = 'offers_distance_cache_v1';

type DistanceCache = Record<string, { km: number; updatedAt: number }>;

function readDistanceCache(): DistanceCache {
  try {
    const raw = localStorage.getItem(DIST_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as DistanceCache;
  } catch {
    return {};
  }
}

function writeDistanceCache(cache: DistanceCache) {
  try {
    localStorage.setItem(DIST_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Persisting distance cache failed (likely storage quota). Safe to ignore.
    // console.debug('Distance cache persist failed', e);
  }
}

function toRad(deg: number) { return (deg * Math.PI) / 180; }
function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export type UiStudyOffer = {
  id: number;
  contact_id: number;
  contact_name?: string | null;
  address?: string | null; // not provided by backend today
  lat?: number | null;
  lng?: number | null;
  offered_at: string; // created_at
  expires_at: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'WITHDRAWN';
};

function mapBackendToUi(o: BackendStudyOffer): UiStudyOffer {
  const contactObj = o.study?.contact as Partial<{ address_lat?: number | null; address_lng?: number | null }> | undefined;
  return {
    id: o.id,
    contact_id: o.study?.contact?.id ?? o.study?.contact_id ?? 0,
    contact_name: o.study?.contact?.name ?? null,
    address: null,
    lat: contactObj?.address_lat ?? null,
    lng: contactObj?.address_lng ?? null,
    offered_at: o.created_at ?? new Date().toISOString(),
    expires_at: o.expires_at,
    status: (o.status || 'pending').toUpperCase() as UiStudyOffer['status'],
  };
}

interface OffersState {
  offers: UiStudyOffer[];
  lastUpdate: string | null;
  unreadCount: number;
  isOpen: boolean;
  userLocation: { lat: number; lng: number } | null;
  distanceCache: DistanceCache;
  subscription?: { unsubscribe: () => void } | null;
  pollHandle?: number | null;
  // guards to avoid duplicate fetches/subscriptions
  startInFlight?: boolean;
  lastListKey?: string;
  lastListAt?: number;

  setOpen: (open: boolean) => void;
  accept: (id: number) => Promise<boolean>;
  startRealtime: (regionId: number | null) => Promise<void>;
  stopRealtime: () => void;
  ensureLocation: () => Promise<void>;
  getDistanceKm: (offer: UiStudyOffer) => Promise<number | null>;
}

export const useOffersStore = create<OffersState>((set, get) => ({
  offers: [],
  lastUpdate: null,
  unreadCount: 0,
  isOpen: false,
  userLocation: null,
  distanceCache: readDistanceCache(),
  subscription: null,
  startInFlight: false,
  lastListKey: '',
  lastListAt: 0,

  setOpen: (open) => set({ isOpen: open }),

  // Initial list + realtime subscription
  startRealtime: async (regionId: number | null) => {
    // Avoid duplicate starts (StrictMode double mount, focus/visibility re-entry)
    const { startInFlight } = get();
    if (startInFlight) return;
    set({ startInFlight: true });
    try {
      // Avoid duplicate subs
      if (get().subscription || get().pollHandle) return;
      if (!regionId) return;

      // Helper to fetch list with throttle
      const fetchList = async () => {
        const { userLocation, lastListKey, lastListAt } = get();
        const key = JSON.stringify({ r: regionId, s: 'pending', p: 1, pp: 20, lt: userLocation?.lat, lg: userLocation?.lng });
        const now = Date.now();
        if (lastListKey === key && now - (lastListAt || 0) < 1200) {
          return; // throttled
        }
        const pulse = await dashboardService.pulse({ regionId, perPage: 20, include: 'offers' });
        const mapped = (pulse.offers?.data ?? []).map(mapBackendToUi).filter((o) => o.status === 'PENDING');
        set({ offers: mapped, unreadCount: mapped.length, lastListKey: key, lastListAt: now });
      };

      // Initial load: list pending offers (guarded)
      try {
        await fetchList();
      } catch (e) {
        // ignore initial load failure
        // eslint-disable-next-line no-console
        console.debug('offers initial list failed', e);
      }

      // Subscribe to realtime (new offers only)
      const dispatchNotificationsUpdated = () => {
        try {
          window.dispatchEvent(new Event('notifications:updated'));
        } catch (_e) { /* noop */ }
      };

      const subCreated = subscribeOffers(regionId, (offer) => {
        const ui = mapBackendToUi(offer);
        if (ui.status !== 'PENDING') return;
        const current = get().offers;
        // avoid duplicates
        if (current.find((o) => o.id === ui.id)) return;
        const next = [ui, ...current];
        set({ offers: next, unreadCount: next.length });
        dispatchNotificationsUpdated();
        try {
          const prefs = useAuthStore.getState().notifications;
          if (prefs?.soundEnabled) {
            const audio = new Audio('/notification.mp3');
            void audio.play();
          }
          if (prefs?.vibrationEnabled) {
            navigator.vibrate?.([150]);
          }
        } catch (_err) {
          /* ignore notification sound/vibration errors */
        }
      });

      // Subscribe to accepted/cancelled/expired to remove from list
      const removeById = (offerId: number) => {
        const current = get().offers;
        if (!current || current.length === 0) return;
        const next = current.filter(o => o.id !== offerId);
        if (next.length !== current.length) {
          set({ offers: next, unreadCount: next.length });
          dispatchNotificationsUpdated();
        }
      };

      const auth = useAuthStore.getState();
      const currentUserId = auth?.user?.id as number | undefined;
      const subAccepted = subscribeOfferAcceptedDetailed((info) => {
        removeById(info.offerId);
        if (currentUserId && info.offered_by_user_id === currentUserId) {
          const who = info.accepted_by_name || 'uma equipe';
          toast.success(`Sua oferta foi aceita por ${who}.`);
        }
      });
      const subCancelled = subscribeOfferCancelled((offerId) => removeById(offerId));
      const subExpired = subscribeOfferExpired((offerId) => removeById(offerId));

      // Compose unsubscribe into a single handle used by stopRealtime
      set({
        subscription: {
          unsubscribe: () => {
            try { subCreated.unsubscribe(); } catch (_e) { /* noop */ }
            try { subAccepted.unsubscribe(); } catch (_e) { /* noop */ }
            try { subCancelled.unsubscribe(); } catch (_e) { /* noop */ }
            try { subExpired.unsubscribe(); } catch (_e) { /* noop */ }
          }
        }
      });

      // Fallback polling every 15s (5s when tab visible) to catch missed events
      const poll = async () => {
        try {
          const pulse = await dashboardService.pulse({ regionId, perPage: 20, include: 'offers' });
          const mapped = (pulse.offers?.data ?? []).map(mapBackendToUi).filter((o) => o.status === 'PENDING');
          set({ offers: mapped, unreadCount: mapped.length });
        } catch (e) {
          // ignore poll errors
          // eslint-disable-next-line no-console
          console.debug('offers poll failed', e);
        } finally {
          const delay = document.hidden ? 15000 : 5000;
          const handle = window.setTimeout(poll, delay);
          set({ pollHandle: handle });
        }
      };
      // schedule first poll to avoid immediate duplicate request after initial fetch
      {
        const delay = document.hidden ? 15000 : 5000;
        const handle = window.setTimeout(poll, delay);
        set({ pollHandle: handle });
      }

      // Immediate refresh when the tab gains focus or becomes visible (no recursive startRealtime)
      const onFocus = () => { void fetchList(); };
      const onVis = () => { if (!document.hidden) { void fetchList(); } };
      window.addEventListener('focus', onFocus);
      window.addEventListener('visibilitychange', onVis);
      // Store a cleanup hook by shadowing stopRealtime
      const prevStop = get().stopRealtime;
      set({
        stopRealtime: () => {
          window.removeEventListener('focus', onFocus);
          window.removeEventListener('visibilitychange', onVis);
          prevStop();
        },
      });
    } finally {
      set({ startInFlight: false });
    }
  },

  accept: async (id: number) => {
    try {
      const res = await StudyOffersService.accept(id);
      const { offers } = get();
      const next = offers.filter(o => o.id !== id);
      set({ offers: next, unreadCount: next.length });
      try { window.dispatchEvent(new Event('notifications:updated')); } catch (_e) { /* noop */ }
      // Persist highlight for the accepted schedule (study_id)
      try {
        const key = 'schedule_highlight_recent_v1';
        const raw = localStorage.getItem(key);
        const map: Record<string, number> = raw ? JSON.parse(raw) : {};
        const scheduleId = res?.data?.study_id as number | undefined;
        if (scheduleId) {
          map[String(scheduleId)] = Date.now();
          // prune entries older than 24h
          const cutoff = Date.now() - 24 * 3600 * 1000;
          for (const k of Object.keys(map)) {
            if (!map[k] || map[k] < cutoff) delete map[k];
          }
          localStorage.setItem(key, JSON.stringify(map));
        }
      } catch (_e) { /* ignore highlight persist errors */ }
      toast.success(res?.message || 'Oferta aceita com sucesso.');
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Falha ao aceitar a oferta';
      toast.error(msg);
      return false;
    }
  },

  stopRealtime: () => {
    const { subscription, pollHandle } = get();
    try { subscription?.unsubscribe(); } catch (_e) { /* noop */ }
    if (pollHandle) {
      window.clearTimeout(pollHandle);
    }
    set({ subscription: null, pollHandle: null });
  },

  ensureLocation: async () => {
    const { userLocation } = get();
    if (userLocation) return;
    if (!('geolocation' in navigator)) return;
    await new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          set({ userLocation: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          resolve();
        },
        () => resolve(),
        { enableHighAccuracy: true, timeout: 5000 }
      );
    });
  },

  getDistanceKm: async (offer: UiStudyOffer) => {
    const { userLocation, distanceCache } = get();
    if (!offer) return null;
    const key = String(offer.contact_id);

    // return cached if exists (TTL 1 day)
    const cached = distanceCache[key];
    const now = Date.now();
    if (cached && now - cached.updatedAt < 24 * 3600 * 1000) {
      return cached.km;
    }

    // Need location and coords to compute
    let userLoc = userLocation;
    if (!userLoc) {
      await get().ensureLocation();
      userLoc = get().userLocation;
    }
    if (!userLoc || offer.lat == null || offer.lng == null) return null;

    const km = haversine(userLoc.lat, userLoc.lng, offer.lat, offer.lng);
    const newCache: DistanceCache = { ...distanceCache, [key]: { km, updatedAt: now } };
    set({ distanceCache: newCache });
    writeDistanceCache(newCache);
    return km;
  },
}));
